import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(n || 0);

/**
 * Stage 4 — Amber banner shown above KPIs on every page that displays money totals
 * filtered by approved=True. Reads /api/transactions/summary's additive fields:
 *   - unapproved_hidden_count
 *   - unapproved_hidden_total
 *
 * If a parent already has the summary payload, pass it via the `summary` prop to
 * avoid a duplicate fetch. Otherwise the banner fetches on its own.
 *
 * The banner is hidden when there are no unapproved items (count === 0).
 */
export default function ApprovalFilterBanner({ summary: summaryProp = null }) {
  const [summary, setSummary] = useState(summaryProp);

  useEffect(() => {
    if (summaryProp) { setSummary(summaryProp); return; }
    let mounted = true;
    axios
      .get(`${API}/transactions/summary`, { withCredentials: true })
      .then((res) => { if (mounted) setSummary(res.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [summaryProp]);

  const hiddenCount = summary?.unapproved_hidden_count || 0;
  const hiddenTotal = summary?.unapproved_hidden_total || 0;

  if (!summary || hiddenCount === 0) return null;

  return (
    <div
      className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-center justify-between gap-4"
      data-testid="approval-filter-banner"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-amber-900" style={{ fontFamily: "Manrope" }}>
            Showing approved items only
          </p>
          <p className="text-xs text-amber-700 mt-0.5">
            <span data-testid="approval-banner-hidden-count">{hiddenCount}</span> item{hiddenCount === 1 ? "" : "s"} hidden
            {" "}(<span data-testid="approval-banner-hidden-total">{fmt(hiddenTotal)}</span> unapproved). Approve them on the Transactions page to include in your totals.
          </p>
        </div>
      </div>
      <Link
        to="/app/transactions"
        data-testid="approval-banner-review-link"
        className="text-xs font-semibold text-amber-800 hover:text-amber-900 bg-white border border-amber-300 rounded-lg px-3 py-2 flex items-center gap-1 whitespace-nowrap transition-colors"
      >
        Review <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

/**
 * Tiny grey context line shown directly under each KPI value, of the form:
 *   "$X total · $Y approved"
 * where X is the unfiltered (all-txn) total and Y is the approved-only total.
 */
export function KpiUnfilteredHint({ unfiltered, approved, testId }) {
  if (!unfiltered && !approved) return null;
  return (
    <p
      className="text-[11px] text-slate-400 mt-1"
      data-testid={testId}
    >
      {fmt(unfiltered)} total · {fmt(approved)} approved
    </p>
  );
}
